import { MultiStepForm } from '@components/form';
import { withPageBase } from '@components/layout';
import { CircleLoader } from '@components/ui';
import { GeneralHelper } from '@helpers';
import {
  DataCollectionShownStep,
  useDataCollectionModel,
  useDataCollectionNavigation,
  useDataCollectionOptions,
  useDataCollectionQuestionnaire,
  useTestSection,
} from '@hooks/questionnaire';
import { Complete, DatasetSelectionStep, ExitConfirmModal } from '@sections/data-collection';
import { TestSection } from '@sections/data-collection/TestSection/TestSection';
import { initialState, questionnaireStore } from '@stores';
import { notionStore } from '@stores/notion.store';
import { AppRoutes } from '@types';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import styles from './DataCollection.module.scss';

export const ASSESSMENTS_IDS_KEY = 'assessmentsIds';

const DataCollection = observer(() => {
  const { push } = useRouter();
  const { values } = notionStore;

  const { isDirectSubmit } = useTestSection();

  const {
    currentStep,
    maxStep,
    selectedRiskMetrics: _selectedRiskMetrics,
    questionnaireData,
    setCurrentStep,
    setMaxStep,
    setQuestionnaireData,
  } = questionnaireStore;

  const [shownStep, setShownStep] = useState<DataCollectionShownStep>('datasetInventory');
  const [completed, setCompleted] = useState(false);
  const [isToggledDetailView, setIsToggledDetailView] = useState(true);

  const { options } = useDataCollectionOptions();

  const { showExitModal, setShowExitModal, redirectUrl, explicitelyLeavePage } = useDataCollectionNavigation({
    completed,
    shownStep,
    setShownStep,
  });

  const { isPreselecting } = useDataCollectionModel({ options, setShownStep, setCompleted });

  const { formMethods, submitting, isLongAssessment, stepsWithHandlers, submitData } = useDataCollectionQuestionnaire({
    options,
    setShownStep,
    setCompleted,
  });

  useEffect(() => {
    setCurrentStep(1);
    setMaxStep(1);

    return () => {
      setQuestionnaireData(initialState); // Reset the questionnaire data when user leaves the page
    };
  }, [setCurrentStep, setMaxStep, setQuestionnaireData]);

  useEffect(() => {
    GeneralHelper.scrollToTop();
  }, [currentStep, shownStep]);

  if (!(options && values && !isPreselecting)) {
    return (
      <div className={styles.loaderWrapper}>
        <CircleLoader />
      </div>
    );
  }

  return (
    <>
      {!completed && <div className={styles.headerLabel}>Calvin</div>}
      {completed ? (
        <Complete
          isLongAssessment={isLongAssessment}
          onStartNew={() => {
            setQuestionnaireData(initialState);
            setCompleted(false);
            setShownStep('datasetInventory');
          }}
        />
      ) : (
        <>
          {shownStep === 'datasetInventory' && (
            <DatasetSelectionStep
              onBack={() => push(AppRoutes.dashboard)}
              onNext={() => setShownStep('testing')}
            />
          )}
          {shownStep === 'testing' && (
            <TestSection
              isToggledDetailView={isToggledDetailView}
              setIsToggledDetailView={setIsToggledDetailView}
              isSubmitting={submitting}
              onNext={() => {
                if (submitting) return;
                if (isDirectSubmit) {
                  submitData(questionnaireData);
                  return;
                }
                setShownStep('questionnaire');
              }}
              onBack={() => setShownStep('datasetInventory')}
            />
          )}
          {shownStep === 'questionnaire' && (
            <FormProvider {...formMethods}>
              <MultiStepForm
                currentStep={currentStep}
                maxStep={maxStep}
                onStepChange={setCurrentStep}
                modelQuestionnaire
                hideDraft
                disabledNext={submitting}
                loadingNext={submitting}
                hideControls={options?.models?.length === 0}
                button={{ label: 'Add model', url: AppRoutes.aiModelAdd }}
                steps={stepsWithHandlers}
              />
            </FormProvider>
          )}
        </>
      )}
      <ExitConfirmModal
        showExitModal={showExitModal}
        onClose={() => setShowExitModal(false)}
        redirectUrl={redirectUrl}
        explicitelyLeavePage={explicitelyLeavePage}
      />
    </>
  );
});

export default withPageBase(DataCollection);
